import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Container, Stack, Tabs } from '@mantine/core';
import PageTitle from '@/components/common/PageTitle';
import { IconBuilding, IconCertificate, IconCoin, IconFileText, IconUsers } from '@tabler/icons-react';
import { ROUTES } from '@/routes/paths';
import { useIsAgent, useIsCarrier, useIsManager } from '@/hooks/useRoles';
import tabClasses from '@/components/common/scrollTabs.module.css';

export type CompanyTabValue = 'card' | 'banking' | 'accreditation' | 'agency' | 'employees';

/** Видна ли вкладка «Моя компания» при текущих ролях. Роли AGENT/CARRIER взаимоисключающие. */
export function isCompanyTabVisible(
  value: CompanyTabValue,
  roles: { readonly isAgent: boolean; readonly isCarrier: boolean; readonly isManager: boolean },
): boolean {
  switch (value) {
    case 'accreditation':
      // Агенту аттестация не нужна; перевозчик проходит аттестацию — вкладку видит.
      return !roles.isAgent;
    case 'agency':
      // Агентский договор не нужен ни агенту, ни перевозчику.
      return !roles.isAgent && !roles.isCarrier;
    case 'employees':
      // Только владелец-менеджер управляет штатом.
      return roles.isManager;
    default:
      // card, banking — всегда.
      return true;
  }
}

// «Моя компания»: заголовок + под-вкладки (О компании / Реквизиты / Аттестация / Договор /
// Сотрудники) + Outlet. Видимость вкладок по ролям — в isCompanyTabVisible: сотрудники — только
// владельцу-менеджеру; аттестация — не агенту; агентский договор — ни агенту, ни перевозчику.
export default function CompanyLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isManager = useIsManager();
  const isAgent = useIsAgent();
  const isCarrier = useIsCarrier();
  const roles = { isAgent, isCarrier, isManager };

  const active = location.pathname.endsWith('/banking')
    ? 'banking'
    : location.pathname.endsWith('/accreditation')
      ? 'accreditation'
      : location.pathname.endsWith('/agency')
        ? 'agency'
        : location.pathname.endsWith('/employees')
          ? 'employees'
          : 'card';

  function handleChange(value: string | null): void {
    if (value === 'banking') {
      navigate(ROUTES.APP_COMPANY_BANKING);
    } else if (value === 'accreditation') {
      navigate(ROUTES.APP_COMPANY_ACCREDITATION);
    } else if (value === 'agency') {
      navigate(ROUTES.APP_COMPANY_AGENCY);
    } else if (value === 'employees') {
      navigate(ROUTES.APP_COMPANY_EMPLOYEES);
    } else {
      navigate(ROUTES.APP_COMPANY);
    }
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <PageTitle>{t('app.account.company')}</PageTitle>

        <Tabs value={active} onChange={handleChange}>
          <Tabs.List className={tabClasses.list}>
            <Tabs.Tab value="card" leftSection={<IconBuilding size={16} />}>
              {t('app.company.tabs.card')}
            </Tabs.Tab>
            <Tabs.Tab value="banking" leftSection={<IconCoin size={16} />}>
              {t('app.company.tabs.banking')}
            </Tabs.Tab>
            {isCompanyTabVisible('accreditation', roles) ? (
              <Tabs.Tab value="accreditation" leftSection={<IconCertificate size={16} />}>
                {t('app.company.tabs.accreditation')}
              </Tabs.Tab>
            ) : null}
            {isCompanyTabVisible('agency', roles) ? (
              <Tabs.Tab value="agency" leftSection={<IconFileText size={16} />}>
                {t('app.company.tabs.agency')}
              </Tabs.Tab>
            ) : null}
            {isCompanyTabVisible('employees', roles) ? (
              <Tabs.Tab value="employees" leftSection={<IconUsers size={16} />}>
                {t('app.company.tabs.employees')}
              </Tabs.Tab>
            ) : null}
          </Tabs.List>
        </Tabs>

        <Outlet />
      </Stack>
    </Container>
  );
}
